import { useState, useCallback } from 'react';
import { trackPaymentRedirect } from 'metrics/user-analytics/errorTracker';
import evertec from '../../../../payments/core/engines/evertec';

/**
 * useEvertecPayment hook.
 * Encapsulate the Evertec SDK lifecycle: session creation, SDK initialization,
 * payment attempt dispatch, and lightbox event handling.
 * @param {object} params - Hook parameters.
 * @param {object} params.purchase - Purchase state object.
 * @param {object} params.env - Environment configuration.
 * @param {object} params.purchaser - Purchaser data (name, email, phone, document).
 * @param {Function} params.onEvertecPaymentAttempt - Dispatch evertecPaymentAttempt action.
 * @param {Function} params.onConfirmEvertecPayment - Dispatch confirmEvertecPayment action.
 * @param {Function} params.onEvertecPaymentFailure - Dispatch Evertec failure error.
 * @returns {object} executePayment function and isLoading state.
 */
function useEvertecPayment({
  purchase,
  env,
  purchaser,
  onEvertecPaymentAttempt,
  onConfirmEvertecPayment,
  onEvertecPaymentFailure,
}) {
  const [isLoading, setIsLoading] = useState(false);

  const executePayment = useCallback(async () => {
    setIsLoading(true);

    const { token, total, selectedPaymentMethod } = purchase;
    const { type, provider } = selectedPaymentMethod || {};
    const {
      api: { purchaseUrl },
      brand,
      currency,
    } = env;

    const description = 'ecommerce & apps for bus tickets';
    const returnUrl = `${window.location.origin}/purchase/${token}/complete`;
    const cancelUrl = `${window.location.origin}/payment/${token}/canceled`;

    try {
      const resp = await evertec.createSession({
        purchaseUrl,
        brand,
        purchaseId: token,
        description,
        currency,
        amount: total,
        returnUrl,
        cancelUrl,
        paymentType: type,
        purchaser,
      });

      if (!resp || resp.status === 'failure') {
        setIsLoading(false);
        onEvertecPaymentFailure();
        return;
      }

      const { process_url: processUrl, request_id: requestId } = resp;

      let attempt;
      try {
        attempt = await onEvertecPaymentAttempt({
          purchaseToken: token,
          type,
          provider,
          requestId,
        });
      } catch {
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      // Purchase refused the payment and the checkout recovers on its own (MER-701): the session
      // was created for that payment, so the buyer must not reach Evertec.
      if (attempt?.handled) return;

      if (type === 'transfer') {
        trackPaymentRedirect({ provider: 'evertec', isExpected: true });
        window.location.href = processUrl;
      } else {
        trackPaymentRedirect({ provider: 'evertec', isExpected: true });
        await evertec.openLightbox({
          processUrl,
          onRejected: () => {
            window.location.href = `/payment/${token}/canceled`;
          },
          onApproved: onConfirmEvertecPayment,
          onClose: () => {
            setIsLoading(false);
          },
        });
      }
    } catch {
      setIsLoading(false);
      onEvertecPaymentFailure();
    }
  }, [
    purchase,
    env,
    purchaser,
    onEvertecPaymentAttempt,
    onConfirmEvertecPayment,
    onEvertecPaymentFailure,
  ]);

  return { executePayment, isLoading };
}

export default useEvertecPayment;
